import { Delete, Edit } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import SellIcon from '@mui/icons-material/Sell';
import { Chip, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Roles } from 'enums';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLinkAlt from 'features/ui/base-components/pai-link-alt';
import PaiRoundTextField from 'features/ui/base-components/pai-round-text-field';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { RoleColorMap, User } from './account-management.types';

const UserItemSkeleton: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.userItem}>
      <Stack className={classes.userItemHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Skeleton variant="text" width={150} height={30} />
          <Skeleton variant="rectangular" width={80} height={24.5} sx={{ borderRadius: '20px' }} />
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Stack>
      </Stack>
      <Stack className={classes.userItemContent} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="circular" width={20} height={21} />
          <Skeleton variant="text" width={200} height={21} />
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Skeleton variant="circular" width={20} height={21} />
          <Skeleton variant="text" width={200} height={21} />
        </Stack>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Skeleton variant="circular" width={20} height={21} />
            <Skeleton variant="text" width={60} height={21} />
          </Stack>
          <Skeleton variant="text" width={150} height={21} />
        </Stack>
      </Stack>
    </div>
  );
};

interface ItemProps {
  user: User;
  disableEdit: boolean;
  isOwnUser: boolean;

  handleOpenConfirmDeleteUserDialog(): void;

  handleOpenEditUserDialog(): void;

  handleOpenManageUserLicensesDialog(): void;

  handleResendInvitation(): void;

  resendInvitationLoading: boolean;
}

const UserItem: React.FC<ItemProps> = ({
  user,
  disableEdit,
  isOwnUser,
  handleOpenConfirmDeleteUserDialog,
  handleOpenEditUserDialog,
  handleOpenManageUserLicensesDialog,
  handleResendInvitation,
  resendInvitationLoading,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { firstname, lastname, role, email, language, createdAt, licenses, isActive, invitationStatus } = user;
  const canResendInvitation = invitationStatus === 'pending' || invitationStatus === 'expired';

  return (
    <div className={classes.userItem}>
      <Stack className={classes.userItemHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Typography className={classes.userName}>
            {firstname} {lastname}
          </Typography>
          <div className={cx(classes.userRole, role)}>{t(role)}</div>
          <Chip
            className={cx(classes.statusChip, isActive ? 'active' : 'inactive')}
            label={isActive ? t('active') : t('inactive')}
            size={'small'}
          />
          {invitationStatus && invitationStatus !== 'none' ? (
            <Chip className={cx(classes.statusChip, invitationStatus)} label={t(`invitationStatus_${invitationStatus}`)} size={'small'} />
          ) : null}
        </Stack>
        {!isOwnUser && (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <IconButton className={classes.iconButton} onClick={handleOpenEditUserDialog} disabled={disableEdit}>
              <Edit />
            </IconButton>
            <IconButton className={cx(classes.iconButton, 'delete')} onClick={handleOpenConfirmDeleteUserDialog} disabled={disableEdit}>
              <Delete />
            </IconButton>
          </Stack>
        )}
      </Stack>
      <Stack className={classes.userItemContent} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <EmailIcon className={classes.icon} />
          <Typography className={classes.secondaryText}>{email}</Typography>
        </Stack>
        {!_.isNil(licenses) ? (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <SellIcon className={classes.icon} />
            <PaiLinkAlt
              underline={'hover'}
              onClick={handleOpenManageUserLicensesDialog}
              sx={(theme) => ({ fontSize: theme.typography.pxToRem(14) })}
              disabled={disableEdit}
            >
              {`${licenses.length} ${licenses.length === 1 ? t('license') : t('licenses')}`}
            </PaiLinkAlt>
          </Stack>
        ) : null}
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <GTranslateIcon className={classes.icon} />
            <Typography className={classes.secondaryText}>{t(language)}</Typography>
          </Stack>
          <Typography className={classes.primaryText}>{`${t('created')}: ${formatDateTime(new Date(createdAt).toISOString(), 'Pp')}`}</Typography>
        </Stack>
        {canResendInvitation ? (
          <PaiButton
            text={t('resendInvitation')}
            size={'small'}
            border={'rounded'}
            action={handleResendInvitation}
            loading={resendInvitationLoading}
            disabled={disableEdit}
          />
        ) : null}
      </Stack>
    </div>
  );
};

interface Props {
  users: User[];
  loading: boolean;
  total: number;
  itemsPerPage: number;
  page: number;
  searchParam: string;

  handleChangePage(event: unknown, newPage: number): void;

  handleSearchParamChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyDown(): void;

  searchKeyUp(): void;

  handleClearSearchField(): void;

  handleOpenConfirmDeleteUserDialog(user: User): () => void;

  handleOpenCreateUserDialog(): void;

  handleOpenEditUserDialog(user: User): () => void;

  handleOpenManageUserLicensesDialog(user: User): () => void;

  handleResendInvitation(user: User): () => void;

  resendingInvitationUserId: string | null;
}

const ManageUsers: React.FC<Props> = ({
  users,
  loading,
  total,
  itemsPerPage,
  page,
  searchParam,
  handleChangePage,
  handleSearchParamChange,
  searchKeyDown,
  searchKeyUp,
  handleClearSearchField,
  handleOpenConfirmDeleteUserDialog,
  handleOpenCreateUserDialog,
  handleOpenEditUserDialog,
  handleOpenManageUserLicensesDialog,
  handleResendInvitation,
  resendingInvitationUserId,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const usersListRef = useRef<HTMLDivElement>(null);

  const { role: userRole, email: userEmail } = useUserData();

  useEffect(() => {
    usersListRef.current?.scrollTo({ top: 0 });
  }, [page]);

  return (
    <div className={classes.manageUsers}>
      <Stack className={classes.listHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
        <Typography className={classes.blockTitle}>{t('userListTitle')}</Typography>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <PaiRoundTextField
            value={searchParam}
            variant={'search'}
            placeholder={t('enterUserNameOrEmailHere')}
            onClear={handleClearSearchField}
            onChange={handleSearchParamChange}
            onKeyDown={searchKeyDown}
            onKeyUp={searchKeyUp}
            style={{ width: '360px' }}
          />
          <PaiButton text={t('addUser')} size={'small'} border={'rounded'} action={handleOpenCreateUserDialog} />
        </Stack>
      </Stack>
      <Stack ref={usersListRef} className={classes.users} gap={2}>
        {loading
          ? Array.from({ length: 10 }).map((_, index) => <UserItemSkeleton key={index} />)
          : users.map((user) => (
              <UserItem
                key={user.id}
                user={user}
                disableEdit={(user.role === Roles.ADMIN || user.role === Roles.AGENCY) && userRole !== Roles.ADMIN && userRole !== Roles.OWNER}
                isOwnUser={user.email === userEmail}
                handleOpenConfirmDeleteUserDialog={handleOpenConfirmDeleteUserDialog(user)}
                handleOpenEditUserDialog={handleOpenEditUserDialog(user)}
                handleOpenManageUserLicensesDialog={handleOpenManageUserLicensesDialog(user)}
                handleResendInvitation={handleResendInvitation(user)}
                resendInvitationLoading={resendingInvitationUserId === (user.globalUser || user.id)}
              />
            ))}
      </Stack>
      <Stack direction={'row'} justifyContent={'flex-end'} style={{ marginTop: 'auto' }}>
        <PaiPagination total={total} itemsPerPage={itemsPerPage} page={page} handleChangePage={handleChangePage} hidden={total <= itemsPerPage} />
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  manageUsers: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    height: '100%',
    background: '#FFF',
    borderRadius: '12px',
    padding: '28px 32px',
    [theme.breakpoints.down('lg')]: {
      padding: '24px',
    },
  },
  listHeader: {
    flexWrap: 'wrap',
  },
  blockTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 500,
  },
  users: {
    overflow: 'auto',
  },
  userItem: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greys.lighter}`,
  },
  userItemHeader: {
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  userItemContent: {
    padding: '16px',
  },
  userName: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
  },
  userRole: {
    fontSize: theme.typography.pxToRem(11),
    padding: '4px 8px',
    borderRadius: '20px',
    color: '#FFF',
    backgroundColor: '#000',
    fontWeight: 500,
    '&.admin': {
      backgroundColor: RoleColorMap.ADMIN,
    },
    '&.basic': {
      backgroundColor: RoleColorMap.BASIC,
    },
    '&.manager': {
      backgroundColor: RoleColorMap.MANAGER,
    },
    '&.agencyadmin': {
      backgroundColor: RoleColorMap.AGENCY,
    },
    '&.owner': {
      backgroundColor: RoleColorMap.ADMIN,
    },
  },
  statusChip: {
    height: '22px',
    fontSize: theme.typography.pxToRem(11),
    '&.active, &.accepted': {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    },
    '&.inactive, &.expired': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
    '&.pending': {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
    },
  },
  iconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.greys.medium,
    },
    '&.delete': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.error.main,
      },
    },
    '&.Mui-disabled': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.action.disabled,
      },
    },
  },
  primaryText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
  },
  secondaryText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.primary,
  },
}));

export default ManageUsers;
